import { create } from "zustand";
import { persist } from "zustand/middleware";
import { authApi, authToken } from "../api";
import type { User } from "../types/auth";
import { hasVaultAccess } from "../utils/subscriptionAccess";

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  _hasHydrated: boolean;
  accessDenied: boolean;

  // Actions
  login: (email: string, password: string, turnstileToken?: string) => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
  setHasHydrated: (hasHydrated: boolean) => void;
  setAccessDenied: (denied: boolean) => void;
  clearAccessDenied: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      _hasHydrated: false,
      accessDenied: false,

      setHasHydrated: (hasHydrated: boolean) => {
        set({ _hasHydrated: hasHydrated });
      },

      login: async (email: string, password: string, turnstileToken?: string) => {
        set({ isLoading: true, error: null });

        try {
          const payload = {
            email,
            password,
            // Only include cf_turnstile_response when token is provided (Turnstile enabled)
            // When Turnstile is disabled, turnstileToken is undefined and field is excluded
            ...(turnstileToken && { cf_turnstile_response: turnstileToken }),
          };

          const response = await authApi.login(payload);

          if (response.success && response.data?.token) {
            // Store token first
            authToken.set(response.data.token);
            
            // Fetch current user to get full user data with roles, membership, and orders
            // Note: Backend returns user directly, not wrapped in { user: ... }
            const userResponse = await authApi.getCurrentUser();
            const fullUser = userResponse.success && userResponse.data
              ? userResponse.data
              : response.data.user || null;

            // Check subscription access — same logic for all roles
            if (!hasVaultAccess(fullUser)) {
              authToken.clear();
              set({
                user: null,
                token: null,
                isAuthenticated: false,
                isLoading: false,
                accessDenied: true,
              });
              return false;
            }

            set({
              token: response.data.token,
              user: fullUser,
              isAuthenticated: true,
              isLoading: false,
            });
            return true;
          } else {
            set({
              error: response.error || "Login failed",
              isLoading: false,
              isAuthenticated: false,
            });
            return false;
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : "Login failed",
            isLoading: false,
            isAuthenticated: false,
          });
          return false;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await authApi.logout();
        } finally {
          authToken.clear();
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      clearError: () => set({ error: null }),

      setAccessDenied: (denied: boolean) => set({ accessDenied: denied }),

      clearAccessDenied: () => set({ accessDenied: false }),
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);
